import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { ProjectItem, ProjectsSearchResponse, ProjectPostItem, ProjectPostSearchResponse, ProjectImageGetResponse, ProjectImageSearchResponse } from '../Domain/entity/ProjectTypes';
import { getYouTubeThumbnail } from '../../../Utils/youtube';
import { createProject, getProject, searchProjects, searchWithdrawProjects, updateProject, searchProjectPosts, createProjectPost, updateProjectPost, deleteProjectPost, searchProjectImages } from './projectActions';

type ProjectStatus = 'idle' | 'loading' | 'succeeded' | 'error';

/**
 * NOTE
 * - Store 전용 경량 타입 사용
 */
type ProjectMetaLite = {
  perPage?: number;
  totalPages?: number;
  numberOfPageElements?: number;
  totalElements?: number;
};

type ProjectStoreItem = {
  _id?: string;
  project_id?: string;
  partner?: string;
  title?: string;
  project_desc?: string;
  status?: string;
  type?: string;
  project_type?: string;
  start_date?: string;
  end_date?: string;
  created_at?: string;
  updated_at?: string;
  goal_amount?: string | number;
  currency_type?: string;
  is_image_display?: boolean | string;
  image_url?: string;
  video_url?: string;
  user_id?: string;
  user_full_name?: string;
  keyman?: boolean | string;
  is_support?: boolean;
  // NOTE: support_project는 서버가 유연한 JSON을 내려줘서 store에서는 얕은 타입으로 보관
  support_project?: Record<string, unknown>;
  subm?: Array<Record<string, any>>;
  // withdraw-related fields (needed in ProjectManagement pay-flow resolution)
  withdraw_id?: string;
  is_sent_authentication_code?: boolean | string;
  is_response_authentication_code?: boolean | string;
  is_sent_fund?: boolean | string;
  user_trials?: number | string;
  trials?: number | string;
  is_active?: boolean | string;
  __optimistic?: boolean;
  accumulated_amount?: number;
};

const normalizeProjectDesc = (
  projectDesc?: string,
  supportProject?: Record<string, unknown>,
) => {
  const direct = typeof projectDesc === 'string' ? projectDesc : '';
  const supportDesc =
    typeof supportProject?.project_desc === 'string' ? String(supportProject.project_desc) : '';
  const raw = direct.trim() ? direct : supportDesc;
  if (!raw) return undefined;
  return raw.replace(/\r\n|\r|\n/g, '<br />');
};

const toStoreProject = (p: ProjectItem): ProjectStoreItem => {
  const supportProject = (p.support_project as Record<string, unknown> | undefined) ?? undefined;
  return {
    _id: p._id,
    project_id: p.project_id,
    partner: p.partner,
    title: p.title,
    project_desc: normalizeProjectDesc(p.project_desc, supportProject),
    status: p.status,
    type: p.type ?? p.project_type,
    project_type: p.project_type,
    start_date: p.start_date,
    end_date: p.end_date,
    created_at: p.created_at,
    updated_at: p.updated_at,
    goal_amount: p.goal_amount,
    currency_type: p.currency_type,
    is_image_display: p.is_image_display,
    image_url: p.image_url,
    video_url: p.video_url,
    user_id: p.user_id,
    user_full_name: p.user_full_name,
    keyman: p.keyman,
    is_support: p.is_support,
    support_project: supportProject,
    subm: (p.subm as Array<Record<string, any>> | undefined) ?? undefined,
    withdraw_id: p.withdraw_id,
    is_sent_authentication_code: p.is_sent_authentication_code as boolean | string | undefined,
    is_response_authentication_code: p.is_response_authentication_code as boolean | string | undefined,
    is_sent_fund: p.is_sent_fund as boolean | string | undefined,
    user_trials: p.user_trials as number | string | undefined,
    trials: p.trials as number | string | undefined,
    is_active: p.is_active as boolean | string | undefined,
    accumulated_amount: p.accumulated_amount,
  };
};

export type ProjectState = {
  status: ProjectStatus;
  getProjectStatus: ProjectStatus;
  updateProjectStatus: ProjectStatus;
  error?: string;

  // list state
  projects: ProjectStoreItem[];
  projectsMeta?: ProjectMetaLite;

  // detail state
  selectedProject?: ProjectStoreItem;
  projectImages: ProjectImageGetResponse[];
  searchProjectImagesStatus: ProjectStatus;

  // posts state
  posts: ProjectPostItem[];
  postsMeta?: ProjectMetaLite;
  searchPostsStatus: ProjectStatus;
  createPostStatus: ProjectStatus;
  updatePostStatus: ProjectStatus;
  deletePostStatus: ProjectStatus;
};

const initialState: ProjectState = {
  status: 'idle',
  getProjectStatus: 'idle',
  updateProjectStatus: 'idle',
  projects: [],
  projectImages: [],
  searchProjectImagesStatus: 'idle',
  posts: [],
  searchPostsStatus: 'idle',
  createPostStatus: 'idle',
  updatePostStatus: 'idle',
  deletePostStatus: 'idle',
};

const projectSlice = createSlice({
  name: 'project',
  initialState,
  reducers: {
    resetProjectState() {
      return initialState;
    },
    clearProjectError(state) {
      state.error = undefined;
    },
    // optimistic helper
    removeProjectById(state, action: PayloadAction<string>) {
      state.projects = state.projects.filter((p) => p.project_id !== action.payload);
    },
    clearPosts(state) {
      state.posts = [];
      state.postsMeta = undefined;
      state.searchPostsStatus = 'idle';
    },
  },
  extraReducers: (builder: any) => {
    // searchProjectImages
    builder.addCase(searchProjectImages.pending, (state) => {
      state.searchProjectImagesStatus = 'loading';
      state.error = undefined;
    });
    builder.addCase(searchProjectImages.fulfilled, (state, action) => {
      state.searchProjectImagesStatus = 'succeeded';
      const payload = action.payload as ProjectImageSearchResponse;
      state.projectImages = payload.data ?? [];
    });
    builder.addCase(searchProjectImages.rejected, (state, action) => {
      state.searchProjectImagesStatus = 'error';
      state.error = action.payload ?? action.error.message;
    });

    // searchProjects
    builder.addCase(searchProjects.pending, (state) => {
      state.status = 'loading';
      state.error = undefined;
    });
    builder.addCase(searchProjects.fulfilled, (state, action) => {
      state.status = 'succeeded';
      // action payload cast
      const payload = action.payload as ProjectsSearchResponse;
      state.projectsMeta = payload.meta
        ? {
            perPage: payload.meta.perPage,
            totalPages: payload.meta.totalPages,
            numberOfPageElements: payload.meta.numberOfPageElements,
            totalElements: payload.meta.totalElements,
          }
        : undefined;
      state.projects = (payload.data ?? []).map(toStoreProject);
    });
    builder.addCase(searchProjects.rejected, (state, action) => {
      state.status = 'error';
      state.error = action.payload ?? action.error.message;
    });

    // searchWithdrawProjects
    builder.addCase(searchWithdrawProjects.pending, (state) => {
      state.status = 'loading';
      state.error = undefined;
    });
    builder.addCase(searchWithdrawProjects.fulfilled, (state, action) => {
      state.status = 'succeeded';
      const payload = action.payload as ProjectsSearchResponse;
      state.projectsMeta = payload.meta
        ? {
            perPage: payload.meta.perPage,
            totalPages: payload.meta.totalPages,
            numberOfPageElements: payload.meta.numberOfPageElements,
            totalElements: payload.meta.totalElements,
          }
        : undefined;
      state.projects = (payload.data ?? []).map(toStoreProject);
    });
    builder.addCase(searchWithdrawProjects.rejected, (state, action) => {
      state.status = 'error';
      state.error = action.payload ?? action.error.message;
    });

    // getProject
    builder.addCase(getProject.pending, (state) => {
      state.getProjectStatus = 'loading';
      state.error = undefined;
    });
    builder.addCase(getProject.fulfilled, (state, action) => {
      state.getProjectStatus = 'succeeded';
      state.selectedProject = toStoreProject(action.payload as ProjectItem);
    });
    builder.addCase(getProject.rejected, (state, action) => {
      state.getProjectStatus = 'error';
      state.error = action.payload ?? action.error.message;
    });

    // createProject (optimistic)
    builder.addCase(createProject.pending, (state, action) => {
      state.status = 'loading';
      state.error = undefined;
      const hasVideoUrl = String(action.meta.arg.video_url ?? '').trim().length > 0;

      // optimistic: temp item add
      const optimisticProject: ProjectStoreItem = {
        project_id: action.meta.requestId,
        title: action.meta.arg.title,
        partner: action.meta.arg.partner,
        project_desc: action.meta.arg.project_desc,
        status: action.meta.arg.status,
        type: action.meta.arg.type,
        start_date: action.meta.arg.start_date,
        end_date: action.meta.arg.end_date,
        goal_amount: action.meta.arg.goal_amount,
        currency_type: action.meta.arg.currency_type,
        is_image_display: !hasVideoUrl,
        image_url: typeof action.meta.arg.image === 'string' ? action.meta.arg.image : undefined,
        video_url: action.meta.arg.video_url,
        user_id: action.meta.arg.user_id,
        user_full_name: action.meta.arg.user_full_name,
        __optimistic: true,
      };

      state.projects = [optimisticProject, ...state.projects];
    });
    builder.addCase(createProject.fulfilled, (state, action) => {
      state.status = 'succeeded';

      const insertedId = action.payload.result_message?.insertedId;
      if (!insertedId) {
        // no insertedId
        return;
      }

      // optimistic id replace
      const optimisticId = action.meta.requestId;
      state.projects = state.projects.map((p) =>
        p.project_id === optimisticId ? { ...p, project_id: insertedId, __optimistic: false } : p,
      );
    });
    builder.addCase(createProject.rejected, (state, action) => {
      state.status = 'error';
      state.error = action.payload ?? action.error.message;

      // optimistic rollback
      const optimisticId = action.meta.requestId;
      state.projects = state.projects.filter((p) => p.project_id !== optimisticId);
    });

    // updateProject
    builder.addCase(updateProject.pending, (state) => {
      state.updateProjectStatus = 'loading';
      state.error = undefined;
    });
    builder.addCase(updateProject.fulfilled, (state, action) => {
      state.updateProjectStatus = 'succeeded';
      // 업데이트된 정보를 selectedProject와 projects 리스트에 반영
      const updated = action.meta.arg;
      const hasVideoUrl = String(updated.video_url ?? '').trim().length > 0;
      const resolvedImageUrl = hasVideoUrl
        ? (getYouTubeThumbnail(String(updated.video_url), 'maxresdefault') ?? updated.image_url)
        : updated.image_url;
      const resolvedIsImageDisplay = updated.is_image_display ?? !hasVideoUrl;

      if (state.selectedProject && state.selectedProject.project_id === updated.project_id) {
        state.selectedProject = {
          ...state.selectedProject,
          title: updated.title ?? state.selectedProject.title,
          project_desc: updated.project_desc ?? state.selectedProject.project_desc,
          goal_amount: updated.goal_amount ?? state.selectedProject.goal_amount,
          currency_type: updated.currency_type ?? state.selectedProject.currency_type,
          video_url: updated.video_url ?? state.selectedProject.video_url,
          image_url: resolvedImageUrl ?? state.selectedProject.image_url,
          is_image_display: resolvedIsImageDisplay ?? state.selectedProject.is_image_display,
        };
      }
      state.projects = state.projects.map((p) =>
        p.project_id === updated.project_id
          ? {
              ...p,
              title: updated.title ?? p.title,
              project_desc: updated.project_desc ?? p.project_desc,
              goal_amount: updated.goal_amount ?? p.goal_amount,
              currency_type: updated.currency_type ?? p.currency_type,
              video_url: updated.video_url ?? p.video_url,
              image_url: resolvedImageUrl ?? p.image_url,
              is_image_display: resolvedIsImageDisplay ?? p.is_image_display,
            }
          : p,
      );
    });
    builder.addCase(updateProject.rejected, (state, action) => {
      state.updateProjectStatus = 'error';
      state.error = action.payload ?? action.error.message;
    });

    // searchProjectPosts
    builder.addCase(searchProjectPosts.pending, (state) => {
      state.searchPostsStatus = 'loading';
      state.error = undefined;
    });
    builder.addCase(searchProjectPosts.fulfilled, (state, action) => {
      state.searchPostsStatus = 'succeeded';
      const payload = action.payload as ProjectPostSearchResponse;
      state.postsMeta = payload.meta
        ? {
            perPage: payload.meta.perPage,
            totalPages: payload.meta.totalPages,
            numberOfPageElements: payload.meta.numberOfPageElements,
            totalElements: payload.meta.totalElements,
          }
        : undefined;
      state.posts = payload.data ?? [];
    });
    builder.addCase(searchProjectPosts.rejected, (state, action) => {
      state.searchPostsStatus = 'error';
      state.error = action.payload ?? action.error.message;
    });

    // createProjectPost
    builder.addCase(createProjectPost.pending, (state) => {
      state.createPostStatus = 'loading';
      state.error = undefined;
    });
    builder.addCase(createProjectPost.fulfilled, (state) => {
      state.createPostStatus = 'succeeded';
    });
    builder.addCase(createProjectPost.rejected, (state, action) => {
      state.createPostStatus = 'error';
      state.error = action.payload ?? action.error.message;
    });

    // updateProjectPost
    builder.addCase(updateProjectPost.pending, (state) => {
      state.updatePostStatus = 'loading';
      state.error = undefined;
    });
    builder.addCase(updateProjectPost.fulfilled, (state, action) => {
      state.updatePostStatus = 'succeeded';
      // 소식 목록에서도 업데이트 반영 (필요시)
      const updated = action.meta.arg;
      state.posts = state.posts.map((p) =>
        p.projectpost_id === updated.projectpost_id
          ? {
              ...p,
              update_message: updated.update_message ?? p.update_message,
              video_url: updated.video_url ?? p.video_url,
              image_url: updated.image_url ?? p.image_url,
              image2_url: updated.image2_url ?? p.image2_url,
              image3_url: updated.image3_url ?? p.image3_url,
              image4_url: updated.image4_url ?? p.image4_url,
              image5_url: updated.image5_url ?? p.image5_url,
              image_desc: updated.image_desc ?? p.image_desc,
              image2_desc: updated.image2_desc ?? p.image2_desc,
              image3_desc: updated.image3_desc ?? p.image3_desc,
              image4_desc: updated.image4_desc ?? p.image4_desc,
              image5_desc: updated.image5_desc ?? p.image5_desc,
              to_sponsor: updated.to_sponsor ?? p.to_sponsor,
              emoticon01: updated.emoticon01 ?? p.emoticon01,
              emoticon02: updated.emoticon02 ?? p.emoticon02,
              emoticon03: updated.emoticon03 ?? p.emoticon03,
              emoticon04: updated.emoticon04 ?? p.emoticon04,
              emoticon05: updated.emoticon05 ?? p.emoticon05,
              cheermsg: updated.cheermsg ?? p.cheermsg,
            }
          : p,
      );
    });
    builder.addCase(updateProjectPost.rejected, (state, action) => {
      state.updatePostStatus = 'error';
      state.error = action.payload ?? action.error.message;
    });

    // deleteProjectPost
    builder.addCase(deleteProjectPost.pending, (state) => {
      state.deletePostStatus = 'loading';
      state.error = undefined;
    });
    builder.addCase(deleteProjectPost.fulfilled, (state, action) => {
      state.deletePostStatus = 'succeeded';
      const deletedId = action.meta.arg.projectpost_id;
      state.posts = state.posts.filter((p) => p.projectpost_id !== deletedId);
    });
    builder.addCase(deleteProjectPost.rejected, (state, action) => {
      state.deletePostStatus = 'error';
      state.error = action.payload ?? action.error.message;
    });
  },
});

export const { resetProjectState, clearProjectError, removeProjectById, clearPosts } = projectSlice.actions;

export default projectSlice.reducer;


